import type { BalanceEvent } from './wallet';
import type { ApiError } from './errors';

/**
 * Socket.IO contract. One connection per tab; auth via the same session cookie (guests may watch, not bet).
 * Namespaced by game: each game adds its events in `games/<game>.ts`; the server merges them in
 * `apps/server/src/realtime/types.ts`.
 * Client → server events use acknowledgements: ack(result | ApiError).
 * Rate limit: token bucket per connection; an event over the limit is rejected with RATE_LIMITED (via ack or `error`).
 */
export interface CoreServerToClient {
  'wallet:balance': (e: BalanceEvent) => void;
  'error': (e: ApiError) => void;
}
export interface CoreClientToServer {
  /** join/leave a game room (room name = GameId) to receive its round events */
  'room:join': (game: string, ack: (r: { ok: true } | ApiError) => void) => void;
  'room:leave': (game: string) => void;
  /**
   * Active-play heartbeat for the daily time limit: send every 60 s while a game screen is visible.
   * Each distinct UTC minute with a ping or a bet counts as one minute of play.
   */
  'activity:ping': () => void;
}

export type Ack<T> = (r: T | ApiError) => void;
export function isApiError(x: unknown): x is ApiError {
  return typeof x === 'object' && x !== null && 'error' in x && typeof (x as ApiError).error === 'string';
}

/** Mutating HTTP requests must carry this header (value `1` or `XMLHttpRequest`) or a same-site Origin (CSRF guard). */
export const CSRF_HEADER = 'x-requested-with';
